/* eslint-disable react-refresh/only-export-components */
// /home/project/src/contexts/EncryptionContext.tsx
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from "react";
import Hex from "crypto-js/enc-hex";
import { EncryptionService } from "../services/EncryptionService";
import { deriveKey, randomHex } from "../utils/cryptoUtils";
import { AuthContext } from "./AuthContext";
import { APP_MODE } from "../config/env";

interface UnlockOpts {
  rememberSession?: boolean;
}

export interface KeyRotationProgress {
  status: 'idle' | 'generating' | 'rotating' | 'success' | 'error';
  message?: string;
  oldKeyBackup?: string;
  newKey?: string;
}

interface Ctx {
  isEnabled: boolean;
  encryptData: (plain: string) => string;
  decryptData: (cipher: string) => string;
  unlockWithPassword: (pw: string, opts?: UnlockOpts) => void;
  unlockWithKey: (hexKey: string, opts?: UnlockOpts) => void;
  lock: () => void;
  generateEncryptionKey: () => string;
  enableEncryption: (key: string) => void;
  disableEncryption: () => void;
  // Key rotation
  rotateKey: () => Promise<{ success: boolean; newKey?: string; error?: string }>;
  rotationProgress: KeyRotationProgress;
  currentKeyFingerprint: string | null;
}

export const EncryptionContext = createContext<Ctx | undefined>(undefined);

const SESSION_KEY = (uid: string) => `ehub_sess_key_${uid}`;
const SESSION_TS = (uid: string) => `ehub_sess_ts_${uid}`;
const MAX_SESSION_MS = 86_400_000; // 24h
const CLIENT_ENCRYPTION_ALLOWED = APP_MODE === 'demo';

const hasSessionStorage = () =>
  typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";

const readSessionItem = (key: string) => {
  if (!hasSessionStorage()) return null;
  try {
    return window.sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const writeSessionItem = (key: string, value: string) => {
  if (!hasSessionStorage()) return;
  try {
    window.sessionStorage.setItem(key, value);
  } catch {
    // ignore unavailable storage
  }
};

const removeSessionItem = (key: string) => {
  if (!hasSessionStorage()) return;
  try {
    window.sessionStorage.removeItem(key);
  } catch {
    // ignore unavailable storage
  }
};

export const EncryptionProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  // Gracefully handle absence of AuthProvider (e.g., isolated tests or provider ordering issues)
  const authCtx = useContext(AuthContext);
  const user = authCtx?.user || null;
  const [svc, setSvc] = useState<EncryptionService | null>(null);
  const [enabled, setEnabled] = useState(false);
  const [currentKeyHex, setCurrentKeyHex] = useState<string | null>(null);
  const [rotationProgress, setRotationProgress] = useState<KeyRotationProgress>({ status: 'idle' });

  const channelRef = React.useRef<BroadcastChannel | null>(null);

  // Compute key fingerprint (first 8 chars of hex for display)
  const currentKeyFingerprint = useMemo(() => {
    if (!currentKeyHex) return null;
    return currentKeyHex.substring(0, 8).toUpperCase();
  }, [currentKeyHex]);
 
 
 
 
 
 

   
   
   
   
   
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const remember = (hexKey:  string, rememberFlag?: boolean) => {
    if (!user) return;
    setCurrentKeyHex(hexKey);
    if (rememberFlag) {
      writeSessionItem(SESSION_KEY(user.id), hexKey);
      writeSessionItem(SESSION_TS(user.id), Date.now().toString());
    }
  };

  const restoreFromSession = useCallback(() => {
    if (!user) return false;
    if (!CLIENT_ENCRYPTION_ALLOWED) {
      removeSessionItem(SESSION_KEY(user.id));
      removeSessionItem(SESSION_TS(user.id));
      return false;
    }
    try {
      const keyHex = readSessionItem(SESSION_KEY(user.id));
      const tsStr = readSessionItem(SESSION_TS(user.id));
      const ts = tsStr ? Number(tsStr) : 0;
      if (keyHex && ts && Date.now() - ts < MAX_SESSION_MS) {
        const keyWA = Hex.parse(keyHex);
        setSvc(new EncryptionService(keyWA));
        setEnabled(true);
        setCurrentKeyHex(keyHex);
        return true;
      }
      removeSessionItem(SESSION_KEY(user.id));
      removeSessionItem(SESSION_TS(user.id));
      setCurrentKeyHex(null);
    } catch {
      // ignore
    }
    return false;
  }, [user]);

  const clearSession = useCallback(() => {
    if (!user) return;
    removeSessionItem(SESSION_KEY(user.id));
    removeSessionItem(SESSION_TS(user.id));
  }, [user]);

  // Reset when user changes; try to auto-unlock from session
  useEffect(() => {
    if (!user) {
      setSvc(null);
      setEnabled(false);
      setCurrentKeyHex(null);
      return;
    }
    restoreFromSession();
  }, [user, restoreFromSession]);

  // Cross-tab sync
  useEffect(() => {
    if (!user) return;

    if ("BroadcastChannel" in window) {
      channelRef.current = new BroadcastChannel("ehub_encryption");
      const onMsg = (e:  MessageEvent) => {
        const data = e.data || {};
        if (data.type === "lock" && data.uid === user.id) {
          setSvc(null);
          setEnabled(false);
        }
        if (data.type === "unlock" && data.uid === user.id) {
          restoreFromSession();
        }
        if (data.type === "key_rotated" && data.uid === user.id) {
          restoreFromSession();
        }
      };
      channelRef.current.addEventListener("message", onMsg);
      return () => channelRef.current?.removeEventListener("message", onMsg);
    }

    const onStorage = () => {
      const hasKey = !!readSessionItem(SESSION_KEY(user.id));
      if (!hasKey) {
        setSvc(null);
        setEnabled(false);
        setCurrentKeyHex(null);
      } else {
        restoreFromSession();
      }
    };
  (window as Window).addEventListener("storage", onStorage);
  return () => (window as Window).removeEventListener("storage", onStorage);
  }, [user, restoreFromSession]);

  const lock = useCallback(() => {
    clearSession();
    setSvc(null);
    setEnabled(false);
    setCurrentKeyHex(null);
    channelRef.current?.postMessage({ type: "lock", uid: user?.id });
  }, [clearSession, user?.id]);

  const unlockWithPassword = useCallback(
    (pw:  string, { rememberSession = false }: UnlockOpts = {}) => {
      if (!CLIENT_ENCRYPTION_ALLOWED) throw new Error('Browser-managed encryption is disabled outside Demo');
      if (!user) throw new Error("login first");
      const salt = randomHex(16);
      const keyWA = deriveKey(pw, salt);
      const keyHex = keyWA.toString();
      setSvc(new EncryptionService(keyWA));
      setEnabled(true);
      remember(keyHex, rememberSession);
      channelRef.current?.postMessage({ type: "unlock", uid: user.id });
    },
    [remember, user]
  );

  const unlockWithKey = useCallback(
    (hexKey:  string, { rememberSession = false }: UnlockOpts = {}) => {
      if (!CLIENT_ENCRYPTION_ALLOWED) throw new Error('Browser-managed encryption is disabled outside Demo');
      if (!user) throw new Error("login first");
      const keyWA = Hex.parse(hexKey);
      setSvc(new EncryptionService(keyWA));
      setEnabled(true);
      remember(hexKey, rememberSession);
      channelRef.current?.postMessage({ type: "unlock", uid: user.id });
    },
    [remember, user]
  );

  const generateEncryptionKey = useCallback(() => {
    if (!CLIENT_ENCRYPTION_ALLOWED) throw new Error('Browser-managed encryption is disabled outside Demo');
    return randomHex(32);
  }, []);

  const enableEncryption = useCallback(
    (key:  string) => {
      if (!CLIENT_ENCRYPTION_ALLOWED) throw new Error('Browser-managed encryption is disabled outside Demo');
      if (!user) throw new Error("login first");
      const keyWA = Hex.parse(key);
      setSvc(new EncryptionService(keyWA));
      setEnabled(true);
      remember(key, true);
      channelRef.current?.postMessage({ type: "unlock", uid: user.id });
    },
    [remember, user]
  );

  const disableEncryption = useCallback(() => {
    lock();
  }, [lock]);

  /**
   * Rotate the workspace encryption key
   * Generates a new key and re-initializes the encryption service
   * In a production system, this would also re-encrypt all stored data
   */
  const rotateKey = useCallback(async (): Promise<{ success: boolean; newKey?: string; error?: string }> => {
    if (!user) {
      return { success: false, error: 'User must be logged in' };
    }
    if (!CLIENT_ENCRYPTION_ALLOWED) {
      return { success: false, error: 'Key rotation requires the server-managed KMS workflow' };
    }
    if (!enabled || !svc) {
      return { success: false, error: 'Encryption must be enabled first' };
    }

    try {
      // Step 1: Generating new key
      setRotationProgress({ status: 'generating', message: 'Generating new encryption key...' });
      await new Promise(resolve => setTimeout(resolve, 500)); // Brief delay for UX

      const newKeyHex = randomHex(32);
      const oldKeyBackup = currentKeyHex || undefined;

      // Step 2: Rotating
      setRotationProgress({ 
        status: 'rotating', 
        message: 'Applying new encryption key...',
        oldKeyBackup,
        newKey: newKeyHex
      });
      await new Promise(resolve => setTimeout(resolve, 800)); // Simulate re-encryption time

      // Apply new key
      const newKeyWA = Hex.parse(newKeyHex);
      setSvc(new EncryptionService(newKeyWA));
      setCurrentKeyHex(newKeyHex);

      // Update session storage with new key
      if (user) {
        writeSessionItem(SESSION_KEY(user.id), newKeyHex);
        writeSessionItem(SESSION_TS(user.id), Date.now().toString());
      }

      // Broadcast key change to other tabs
      channelRef.current?.postMessage({ type: 'key_rotated', uid: user.id });

      // Step 3: Success
      setRotationProgress({ 
        status: 'success', 
        message: 'Key rotation complete',
        oldKeyBackup,
        newKey: newKeyHex
      });

      return { success: true, newKey: newKeyHex };
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error during key rotation';
      setRotationProgress({ status: 'error', message: errorMessage });
      return { success: false, error: errorMessage };
    }
  }, [user, enabled, svc, currentKeyHex]);

  // Reset rotation progress after success/error (for next rotation)
  const _resetRotationProgress = useCallback(() => {
    setRotationProgress({ status: 'idle' });
  }, []);

  // Auto-lock after inactivity
  useEffect(() => {
    if (!enabled || !user) return;
    let timer = window.setTimeout(() => lock(), MAX_SESSION_MS);
    const reset = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => lock(), MAX_SESSION_MS);
      writeSessionItem(SESSION_TS(user.id), Date.now().toString());
    };
    window.addEventListener("mousemove", reset);
    window.addEventListener("keydown", reset);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("mousemove", reset);
      window.removeEventListener("keydown", reset);
    };
  }, [enabled, user, lock]);

  const encryptData = useCallback(
    (plain:  string) => {
      if (!svc) return plain;
      try {
        const out = svc.encrypt(plain);
        return JSON.stringify(out);
      } catch (e) {
        console.error("[encryption] encrypt failed:", e);
        throw new Error('Encryption failed; data was not stored as plaintext');
      }
    },
    [svc]
  );

  const decryptData = useCallback(
    (cipher:  string) => {
      if (!svc) return cipher;
      try {
        const parsed = JSON.parse(cipher);
        return svc.decrypt(parsed);
      } catch {
        return cipher;
      }
    },
    [svc]
  );

  const value = useMemo<Ctx>(
    () => ({
      isEnabled: enabled,
      encryptData,
      decryptData,
      unlockWithPassword,
      unlockWithKey,
      lock,
      generateEncryptionKey,
      enableEncryption,
      disableEncryption,
      rotateKey,
      rotationProgress,
      currentKeyFingerprint,
    }),
    [
      enabled,
      encryptData,
      decryptData,
      unlockWithPassword,
      unlockWithKey,
      lock,
      generateEncryptionKey,
      enableEncryption,
      disableEncryption,
      rotateKey,
      rotationProgress,
      currentKeyFingerprint,
    ]
  );

  return (
    <EncryptionContext.Provider value={value}>
      {children}
    </EncryptionContext.Provider>
  );
};

export const useEncryption = () => {
  const c = useContext(EncryptionContext);
  if (!c) throw new Error("EncryptionProvider missing");
  return c;
};
